/**
 * Mock form preview content for each template.
 * Each section has rows; each row is an array of field labels (fields in one row).
 */

import type {
  GroupField,
  FormField,
  FormRowField,
} from '@components/pages/FormBuilder/types'

export interface FormPreviewSection {
  sectionTitle: string
  rows: string[][]
}

export interface FormPreviewMock {
  formTitle: string
  sections: FormPreviewSection[]
}

function isFormRowField(
  f: FormField | GroupField | FormRowField,
): f is FormRowField {
  return (f as FormRowField).type === 'Form Row' && 'fields' in f
}

function isGroupField(
  f: FormField | GroupField | FormRowField,
): f is GroupField {
  return (
    'fields' in f && !('type' in f && (f as FormRowField).type === 'Form Row')
  )
}

/** Build preview rows from a group's fields (Form Row → one row of labels; Form Field → one row; nested Group → one row with name). */
function fieldsToRows(
  fields: (FormField | GroupField | FormRowField)[],
): string[][] {
  const rows: string[][] = []
  for (const f of fields) {
    if (isFormRowField(f)) {
      const row = (f.fields as FormField[]).map((ff) => ff.label ?? '')
      if (row.some(Boolean)) rows.push(row)
    } else if (isGroupField(f)) {
      rows.push([f.name])
    } else {
      const label = (f as FormField).label ?? ''
      if (label) rows.push([label])
    }
  }
  return rows
}

/**
 * Convert form template schema_json (from API) to FormPreviewMock for the card preview.
 * Handles schema_json as string or GroupField[].
 */
export function schemaToPreview(
  formTitle: string,
  schemaJson: string | GroupField[] | { groups: GroupField[] } | undefined,
): FormPreviewMock | null {
  if (schemaJson == null) return null
  let groups: GroupField[]
  if (Array.isArray(schemaJson)) {
    groups = schemaJson
  } else if (typeof schemaJson === 'object' && 'groups' in schemaJson) {
    groups = Array.isArray(schemaJson.groups) ? schemaJson.groups : []
  } else if (typeof schemaJson === 'string') {
    try {
      const parsed = JSON.parse(schemaJson) as
        | GroupField[]
        | { groups?: GroupField[] }
      groups = Array.isArray(parsed) ? parsed : (parsed.groups ?? [])
    } catch {
      return null
    }
  } else {
    groups = []
  }
  if (!Array.isArray(groups) || groups.length === 0) return null
  return {
    formTitle: formTitle || 'Untitled Form',
    sections: groups.map((g) => ({
      sectionTitle: g.name,
      rows: fieldsToRows(g.fields),
    })),
  }
}

export const TEMPLATE_PREVIEW_MOCKS: Record<string, FormPreviewMock> = {
  'new-patient': {
    formTitle: 'New Patient Form',
    sections: [
      {
        sectionTitle: 'Personal Information',
        rows: [
          ['First name', 'Last name'],
          ['DOB', 'Gender'],
        ],
      },
      {
        sectionTitle: 'Contact Information',
        rows: [['Phone', 'Email']],
      },
      { sectionTitle: 'Residential Address', rows: [['Address']] },
      { sectionTitle: 'Allergies', rows: [['Penicillin', 'Latex', 'Other']] },
      { sectionTitle: "Patient's Signature", rows: [['Signature']] },
    ],
  },
  'return-patient': {
    formTitle: 'Return Patient Form',
    sections: [
      {
        sectionTitle: 'Personal Information',
        rows: [
          ['First name', 'Last name'],
          ['DOB', 'Gender'],
        ],
      },
      {
        sectionTitle: 'Contact Information',
        rows: [['Phone', 'Email']],
      },
      {
        sectionTitle: 'Updates',
        rows: [['Insurance changes', 'Medication changes']],
      },
      { sectionTitle: 'Signature', rows: [['Patient Signature']] },
    ],
  },
  'insurance-info': {
    formTitle: 'Insurance Information',
    sections: [
      {
        sectionTitle: 'Insurance Details',
        rows: [['Provider', 'Policy #', 'Group #']],
      },
      {
        sectionTitle: 'Subscriber',
        rows: [['Name', 'Relationship', 'DOB']],
      },
      { sectionTitle: 'Signature', rows: [['Authorized Signature']] },
    ],
  },
  'general-consent': {
    formTitle: 'General Consent Form',
    sections: [
      {
        sectionTitle: 'Consent Information',
        rows: [['Consent Date'], ['Description']],
      },
      { sectionTitle: 'Acknowledgement', rows: [['Patient Signature']] },
    ],
  },
  hipaa: {
    formTitle: 'HIPAA/Privacy Acknowledgement',
    sections: [
      {
        sectionTitle: 'Privacy Notice',
        rows: [['Notice text'], ['Acknowledgement']],
      },
      { sectionTitle: 'Signature', rows: [['Patient Signature']] },
    ],
  },
  'cancellation-policy': {
    formTitle: 'Appointment Cancellation',
    sections: [
      {
        sectionTitle: 'Policy',
        rows: [['Cancellation terms', 'Notice period']],
      },
      { sectionTitle: 'Acknowledgement', rows: [['Patient Signature']] },
    ],
  },
  'others-general-consent': {
    formTitle: 'General Consent Form',
    sections: [
      {
        sectionTitle: 'Consent Information',
        rows: [['Date'], ['Description']],
      },
      { sectionTitle: 'Signature', rows: [['Patient Signature']] },
    ],
  },
  'others-hipaa': {
    formTitle: 'HIPAA Acknowledgement',
    sections: [
      { sectionTitle: 'Privacy Notice', rows: [['Acknowledgement']] },
      { sectionTitle: 'Signature', rows: [['Patient Signature']] },
    ],
  },
  'dental-history': {
    formTitle: 'Dental History Form',
    sections: [
      {
        sectionTitle: 'Patient Info',
        rows: [['Name', 'DOB'], ['Previous dentist']],
      },
      {
        sectionTitle: 'Dental History',
        rows: [['Treatments', 'X-rays', 'Conditions']],
      },
      { sectionTitle: 'Signature', rows: [['Patient Signature']] },
    ],
  },
  'records-request': {
    formTitle: 'Patient Records Request',
    sections: [
      {
        sectionTitle: 'Requesting Party',
        rows: [['Name', 'Clinic'], ['Contact']],
      },
      {
        sectionTitle: 'Patient',
        rows: [['Name', 'DOB'], ['Records needed']],
      },
      { sectionTitle: 'Authorization', rows: [['Signature', 'Date']] },
    ],
  },
  'records-release': {
    formTitle: 'Patient Records Release',
    sections: [
      {
        sectionTitle: 'Release To',
        rows: [['Name', 'Clinic'], ['Address']],
      },
      {
        sectionTitle: 'Patient',
        rows: [['Name', 'DOB'], ['Records to release']],
      },
      { sectionTitle: 'Authorization', rows: [['Signature', 'Date']] },
    ],
  },
}
